import React, { cloneElement, forwardRef, isValidElement, useCallback, useImperativeHandle, useLayoutEffect, useRef } from "react";
import { useVirtualizer, VirtualItem, ScrollToOptions } from "@tanstack/react-virtual";
import classNames from "classnames";
import mergeRefs from "merge-refs";


export interface VirtualListRef {
    scrollTo: (index: number, allign?: ScrollToOptions['align']) => void;
}

interface VirtualListProps<T = any> {
    className?: string;
    "data-testid"?: string;
    items: T[];
    itemHeight: number;
    visibleItems: number;
    overscan?: number;
    gap?: number;
    getItemKey?: (item: T, index: number) => string | number;
    renderItem: (item: T, index: number, virtualItem: VirtualItem) => React.ReactNode;
}

export const VirtualList = forwardRef<VirtualListRef, VirtualListProps>((props, ref) => {
    const {
        className,
        "data-testid": dataTestId,
        items,
        itemHeight,
        visibleItems,
        overscan = 2,
        gap = 0,
        getItemKey,
        renderItem
    } = props;

    const containerRef = useRef<HTMLDivElement>(null);

    const virtualizer = useVirtualizer({
        count: items.length,
        getScrollElement: () => containerRef.current,
        estimateSize: () => itemHeight,
        gap: gap,
        measureElement: (element: Element) => {
            return Math.max(element.clientHeight, itemHeight);
        },
        overscan,
    });

    useImperativeHandle(ref, () => ({
        scrollTo: (index: number, allign?: ScrollToOptions['align']) => {
            virtualizer.scrollToIndex(index, { align: allign ?? 'auto' });
        }
    }));

    const virtualItems = virtualizer.getVirtualItems();

    const measureRef = useCallback((node: Element | null) => {
        if (node) {
            virtualizer.measureElement(node);
        }
    }, [virtualizer]);

    useLayoutEffect(() => {
        virtualizer?.measure?.();
      }, [virtualizer]);

    return (
        <div ref={containerRef}
             role="list"
             className={classNames("overflow-y-auto h-full", className)}
             style={{ maxHeight: `${visibleItems * itemHeight + (visibleItems - 1) * gap}px` }}
             data-testid={dataTestId}
        >
            <div className="relative w-full"
                 style={{ height: `${virtualizer.getTotalSize()}px` }}
            >{virtualItems.map(virtualItem => {
                const item = items[virtualItem.index];
                const key = getItemKey ? getItemKey(item, virtualItem.index) : virtualItem.index;
                const renderedItem = renderItem(item, virtualItem.index, virtualItem);
                
                const virtualItemStyle: React.CSSProperties = {
                    top: 0,
                    left: 0,
                    width: "100%",
                    position: "absolute",
                    height: `${virtualItem.size}px`,
                    minHeight: "fit-content",
                    transform: `translateY(${virtualItem.start}px)`,
                };

                if (isValidElement(renderedItem)) {
                    const originalRef = (renderedItem as any).ref;
                    const mergedRef = originalRef ? mergeRefs(measureRef, originalRef) : measureRef;

                    try {
                        return cloneElement(renderedItem, {
                            key,
                            ref: mergedRef,
                            'data-index': virtualItem.index,
                            style: {
                                ...renderedItem.props.style,
                                ...virtualItemStyle,
                            }
                        } as any);
                    } catch (error) {
                        // cloneElement 실패 시 div로 fallback
                    }
                }

                return (
                    <div key={key}
                         ref={measureRef}
                         data-index={virtualItem.index}
                         style={virtualItemStyle}
                    >
                        {renderedItem}
                    </div>
                );
            })}</div>
        </div>
    );
});

